// ========= Copyright 2025-2026 @ Eigent.ai All Rights Reserved. =========
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
//     http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.
// ========= Copyright 2025-2026 @ Eigent.ai All Rights Reserved. =========

import { DisclosureTrigger } from '@/components/remote/primitives/Disclosure';
import { MorphIcon } from '@/components/ui/morph-icon';
import { cn } from '@/lib/utils';
import type {
  ToolCallItem,
  WorkItemStatus,
} from '@web/lib/remoteWorkItemModel';
import { Check, CircleAlert, Clock3, Loader2, Pause, Wrench } from 'lucide';
import { useId, useState } from 'react';
import {
  formatDuration,
  WORK_STATUS_CLASS,
  WORK_STATUS_LABEL,
} from './presentation';
import { sanitizeToolText, ToolInputOutputPanel } from './ToolInputOutputPanel';

export interface ToolCallDisclosureProps {
  item: ToolCallItem;
  defaultExpanded?: boolean;
  expanded?: boolean;
  onExpandedChange?: (expanded: boolean) => void;
  className?: string;
}

function ToolStatusGlyph({ status }: { status: WorkItemStatus }) {
  const className = 'h-3.5 w-3.5 shrink-0';
  switch (status) {
    case 'queued':
      return <MorphIcon icon={Clock3} aria-hidden className={className} />;
    case 'in_progress':
      return (
        <MorphIcon
          icon={Loader2}
          aria-hidden
          className={cn(className, 'animate-spin')}
        />
      );
    case 'waiting':
    case 'failed':
      return <MorphIcon icon={CircleAlert} aria-hidden className={className} />;
    case 'completed':
      return <MorphIcon icon={Check} aria-hidden className={className} />;
    case 'interrupted':
      return <MorphIcon icon={Pause} aria-hidden className={className} />;
  }
}

export function ToolCallDisclosure({
  item,
  defaultExpanded = false,
  expanded: controlledExpanded,
  onExpandedChange,
  className,
}: ToolCallDisclosureProps) {
  const [localExpanded, setLocalExpanded] = useState(defaultExpanded);
  const expanded = controlledExpanded ?? localExpanded;
  const setExpanded = (next: boolean) => {
    if (controlledExpanded === undefined) setLocalExpanded(next);
    onExpandedChange?.(next);
  };
  const disclosureId = `tool-call-${useId().replace(/:/g, '')}`;
  const toolName = sanitizeToolText(item.toolName?.trim() || 'Tool call');
  const durationLabel = formatDuration(item.durationMs);

  return (
    <section
      className={cn(
        'min-w-0 overflow-hidden rounded-xl border border-solid border-ds-border-neutral-subtle-default bg-ds-bg-neutral-default-default',
        className
      )}
      aria-label={`${toolName} tool call`}
    >
      <DisclosureTrigger
        expanded={expanded}
        onExpandedChange={setExpanded}
        controlsId={disclosureId}
        ariaLabel={`${expanded ? 'Collapse' : 'Expand'} ${toolName} tool call details · ${WORK_STATUS_LABEL[item.status]}${durationLabel !== '—' ? ` · ${durationLabel}` : ''}${item.error ? ' · Error recorded' : ''}`}
        className="px-3 py-2.5 hover:bg-ds-bg-neutral-subtle-hover"
      >
        <div className="flex min-w-0 items-center gap-2">
          <MorphIcon
            icon={Wrench}
            aria-hidden
            className="h-4 w-4 shrink-0 text-ds-icon-neutral-muted-default"
          />
          <span className="min-w-0 flex-1 truncate text-body-sm font-medium text-ds-text-neutral-default-default">
            {toolName}
          </span>
          <span
            className={cn(
              'inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-label-xs font-semibold',
              WORK_STATUS_CLASS[item.status]
            )}
          >
            <ToolStatusGlyph status={item.status} />
            {WORK_STATUS_LABEL[item.status]}
          </span>
          {item.error && item.status !== 'failed' && (
            <span className="shrink-0 text-label-xs font-semibold text-ds-text-error-default-default">
              Error
            </span>
          )}
          {durationLabel !== '—' && (
            <span className="shrink-0 text-label-xs tabular-nums text-ds-text-neutral-muted-default">
              {durationLabel}
            </span>
          )}
        </div>
      </DisclosureTrigger>

      {expanded && (
        <div
          id={disclosureId}
          role="region"
          aria-label={`${toolName} input and output`}
          className="border-x-0 border-b-0 border-t border-solid border-ds-border-neutral-subtle-default px-3 py-3"
        >
          <ToolInputOutputPanel
            input={item.input}
            output={item.output}
            result={item.result}
            error={item.error}
            exitCode={item.exitCode}
            cwd={item.cwd}
            outputTruncated={item.outputTruncated}
            outputByteCount={item.outputByteCount}
          />
        </div>
      )}
    </section>
  );
}
